import { Link } from "react-router-dom";
import { CalendarDays, CheckSquare, Lock, LogOut, Package, Scale } from "lucide-react";
import { supabase } from "@/lib/supabase";
import { useSession } from "@/hooks/useSession";

type ExcoLink = {
  title: string;
  description: string;
  to?: string;
  icon: React.ReactNode;
  disabled?: boolean;
};

export function ExcoDashboardPage() {
  const { session } = useSession();

  const items: ExcoLink[] = [
    {
      title: "Tasks",
      description: "Track ongoing work, owners, and priorities.",
      to: "/tasks",
      icon: <CheckSquare className="h-5 w-5" />,
    },
    {
      title: "Agenda",
      description: "Propose topics and vote for the next monthly meeting.",
      to: "/agenda",
      icon: <CalendarDays className="h-5 w-5" />,
    },
    {
      title: "Decisions",
      description: "Record decisions made in ExCo / monthly meetings.",
      disabled: true,
      icon: <Scale className="h-5 w-5" />,
    },
    {
      title: "Assets",
      description: "Manage physical resources, inventories, and handovers.",
      disabled: true,
      icon: <Package className="h-5 w-5" />,
    },
  ];

  return (
    <div className="min-h-screen bg-brand-gray text-brand-blue">
      <div className="mx-auto max-w-6xl px-4 py-8">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <p className="text-xs font-bold uppercase tracking-[0.3em] text-brand-maroon">ExCo</p>
            <h1 className="mt-2 font-display text-3xl font-bold text-brand-blue">Dashboard</h1>
            <p className="mt-2 max-w-2xl text-sm text-brand-blue/75">
              Quick links for ExCo members. New modules (Decisions, Assets, etc.) can be added here over time.
            </p>
          </div>

          <div className="flex flex-wrap items-center justify-end gap-2">
            {session?.user?.email ? (
              <div className="hidden max-w-[18rem] truncate rounded-full bg-white px-3 py-2 text-sm font-semibold text-brand-blue/80 shadow-sm ring-1 ring-black/10 md:block">
                {session.user.email}
              </div>
            ) : null}
            <button
              type="button"
              title="Log out"
              aria-label="Log out"
              onClick={() => void supabase.auth.signOut()}
              className="inline-flex h-10 w-10 items-center justify-center rounded-full bg-white text-brand-blue shadow-sm ring-1 ring-black/10 hover:bg-brand-gray/20"
            >
              <LogOut className="h-5 w-5" />
            </button>
          </div>
        </div>

        <div className="mt-8 grid gap-4 md:grid-cols-2">
          {items.map((it) => {
            const card = (
              <div className="rounded-2xl border border-black/10 bg-white p-5 shadow-sm transition hover:-translate-y-[1px] hover:shadow-md">
                <div className="flex items-start justify-between gap-4">
                  <div className="min-w-0">
                    <p className="font-display text-lg font-bold text-brand-blue">{it.title}</p>
                    <p className="mt-1 text-sm text-brand-blue/70">{it.description}</p>
                  </div>
                  <div className="flex h-10 w-10 flex-none items-center justify-center rounded-xl bg-brand-gray/40 text-brand-blue ring-1 ring-black/10">
                    {it.icon}
                  </div>
                </div>
                {it.disabled ? (
                  <div className="mt-4 inline-flex items-center gap-2 rounded-full bg-brand-gray/40 px-3 py-2 text-xs font-semibold text-brand-blue/70 ring-1 ring-black/10">
                    <Lock className="h-4 w-4" />
                    Coming soon
                  </div>
                ) : (
                  <div className="mt-4 inline-flex items-center gap-2 rounded-full bg-brand-maroon px-4 py-2 text-xs font-bold uppercase tracking-wide text-white">
                    Open
                  </div>
                )}
              </div>
            );

            if (it.disabled || !it.to) return <div key={it.title}>{card}</div>;
            return (
              <Link key={it.title} to={it.to} className="block focus:outline-none focus:ring-2 focus:ring-brand-maroon/50 rounded-2xl">
                {card}
              </Link>
            );
          })}
        </div>
      </div>
    </div>
  );
}

